import type { ParseResult } from "papaparse"
import type { PreviewTableCell } from "@/components/Grid/PreviewTable.vue"

import type { CSVObj, UploadFile } from "@/types"
import { CellDisplayKind } from "@kaleidoscope-solutions/engine-wasm"
import { parse } from "papaparse"
import * as XLSX from "xlsx"

/**
 * Returns the date format of the users browser local eg. DD/MM/YYYY
 * @returns string
 */
function getDateFormatString() {
  const formatObj = new Intl.DateTimeFormat("default").formatToParts(new Date())
  const partMap: Record<string, string> = {
    day: "DD",
    month: "MM",
    year: "YYYY",
  }

  return formatObj.map((obj) => partMap[obj.type] ?? obj.value).join("")
}

export function readXlsxFile(file: File): Promise<UploadFile> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.readAsArrayBuffer(file)

    const usersDateFormat = getDateFormatString() || "YYYY/MM/DD"

    fileReader.onload = () => {
      const arrayBuffer = <ArrayBuffer>fileReader.result
      try {
        const xlsxWorkbook = XLSX.read(arrayBuffer, {
          type: "binary",
          // Make sure cell that are dates are treated as dates in the XLSX library.
          cellDates: true,
          raw: false,
          // Make sure dates aren't used in the string format.
          cellText: false,
        })

        if (xlsxWorkbook === null) {
          reject(new Error("xlsxWorkbook not found"))
          return
        }

        if (xlsxWorkbook.SheetNames.length > 1) {
          reject(new Error("Only 1 sheet supported"))
        }

        const firstSheet = xlsxWorkbook.Sheets[xlsxWorkbook.SheetNames[0]]

        const csvString = XLSX.utils.sheet_to_csv(firstSheet, {
          // Date format to show the user and parse in the engine.
          dateNF: usersDateFormat,
          blankrows: false
        })

        const csvParse: ParseResult<string[]> = parse(csvString)

        const dataAsArray = csvParse.data

        const scraped = rowsColsToScraped(dataAsArray)

        resolve({
          file,
          dataAsArray,
          scraped,
        })
      } catch (error) {
        reject(error)
      }
    }

    fileReader.onerror = () => {
      reject(new Error("Error reading file"))
    }
  })
}

export function readCSV(file: File): Promise<UploadFile> {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader()

    fileReader.readAsText(file)

    fileReader.onload = () => {
      const csv: string = fileReader.result as string
      const parsed = parse<string[]>(csv, { skipEmptyLines: "greedy" })

      // get Array of Array table/sheet/workbooks
      const dataAsArray: string[][] = parsed.data
      const scraped: CSVObj = rowsColsToScraped(dataAsArray)

      resolve({
        file,
        dataAsArray,
        scraped,
      })
    }

    fileReader.onerror = () => {
      reject(new Error("Error reading file"))
    }
  })
}

export function aoaSheetToTableFormat(
  aoaSheet: string[][]
): PreviewTableCell[][] {
  const tableFormat = []
  for (let r = 0; r < aoaSheet.length; r++) {
    const row = aoaSheet[r]

    const tableRow = []
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]

      tableRow.push({
        displayKind:
          r === 0 ? CellDisplayKind.ColumnHeader : CellDisplayKind.Cell,
        value: cell,
      })
    }

    tableFormat.push(tableRow)
  }

  return tableFormat
}

function rowsColsToScraped(parsedData: string[][]): CSVObj {
  const cleanedData = removeAllEmptyColumns(<string[][]>parsedData)
  const headers = cleanedData[0]
  const rows = cleanedData.slice(1)
  const sheetHeaders = worksheetHeaders(headers)

  return { sheetHeaders, headers, rows }
}

function worksheetHeaders(headers: string[]): string[] {
  return headers.map((_header, index) => numToAlphaHeading(index))
}

// 0 = A, 1 = B, 26 = AA, 27 = AB, etc...
function numToAlphaHeading(num: number): string {
  let alpha = ""

  for (; num >= 0; num = Number.parseInt((num / 26).toString(), 10) - 1) {
    alpha = String.fromCharCode((num % 26) + 0x41) + alpha
  }

  return alpha
}

function removeAllEmptyColumns(csv: string[][]): string[][] {
  const columnCount = csv.reduce(
    (colCount, row) => Math.max(colCount, row.length),
    0
  )

  if (columnCount === 0) {
    return csv
  }

  for (let columnIndex = columnCount - 1; columnIndex >= 0; columnIndex--) {
    if (isColumnEmpty(columnIndex, csv)) {
      csv.map((row) => row.splice(columnIndex, 1))
    }
  }

  return csv
}

function isColumnEmpty(columnIndex: number, csv: string[][]): boolean {
  return csv.every((row) => row[columnIndex] === "")
}
